import { create } from "zustand";
import { formatServiceDuration } from "@/features/dashboard/settings/services-format";
import type { CartItem, CatalogItem, PosClient, PosTicket } from "./types";
import { normalizeTipPercent } from "./types";

const DEFAULT_TIP_PERCENT = 10;

interface AppointmentCheckoutInput {
  fromAppointment: string;
  serviceName: string;
  priceInCents: number;
  clientId?: string;
  clientName?: string;
  stylistName?: string | null;
  durationMinutes?: number | null;
  serviceId?: string;
}

interface PosStoreState {
  tickets: PosTicket[];
  activeTicketId: string;
  nextNumber: number;
  setActiveTicket: (id: string) => void;
  addTicket: () => void;
  createTicketFromAppointment: (input: AppointmentCheckoutInput) => void;
  removeTicket: (id: string) => void;
  addItem: (item: CatalogItem) => void;
  removeItem: (itemId: string) => void;
  setTip: (percent: number) => void;
  attachClient: (client: PosClient) => void;
}

const ADA_CART: CartItem[] = [
  { id: "svc1", name: "Haircut & blowout", meta: "75 min · Mariana", price: 85, qty: 1 },
  { id: "ad1", name: "Olaplex bond", meta: "add-on · 30 min", price: 45, qty: 1 },
  { id: "r3", name: "Driftwood pomade", meta: "House · 60g", price: 22, qty: 1 },
];

const WALK_IN_CART: CartItem[] = [
  { id: "svc5", name: "Beard trim", meta: "30 min · Mariana", price: 35, qty: 1 },
];

function cloneCart(cart: CartItem[]): CartItem[] {
  return cart.map((line) => ({ ...line }));
}

function createWalkInTicket(number: number): PosTicket {
  return {
    id: `pos-ticket-${number}`,
    number,
    client: null,
    cart: [],
    tipPercent: DEFAULT_TIP_PERCENT,
    status: "walk-in",
    paid: false,
  };
}

function buildCheckoutCartMeta(
  stylistName: string | null | undefined,
  durationMinutes: number | null | undefined,
): string {
  const durationText =
    durationMinutes != null && durationMinutes > 0 ? formatServiceDuration(durationMinutes) : "";
  const stylist = stylistName?.trim();
  if (durationText && stylist) {
    return `${durationText} · ${stylist}`;
  }
  if (durationText) {
    return durationText;
  }
  if (stylist) {
    return stylist;
  }
  return "Appointment";
}

function buildCheckoutCartLine(input: AppointmentCheckoutInput): CartItem {
  return {
    id: input.serviceId ?? `appt-${input.fromAppointment}`,
    name: input.serviceName,
    meta: buildCheckoutCartMeta(input.stylistName, input.durationMinutes),
    price: input.priceInCents / 100,
    qty: 1,
  };
}

function createInitialPosState(): Pick<PosStoreState, "tickets" | "activeTicketId" | "nextNumber"> {
  return {
    tickets: [
      {
        id: "pos-ticket-482",
        number: 482,
        client: { id: "client-ada", name: "Ada Delacroix" },
        cart: cloneCart(ADA_CART),
        tipPercent: DEFAULT_TIP_PERCENT,
        status: "active",
        paid: false,
      },
      {
        id: "pos-ticket-483",
        number: 483,
        client: { id: "client-jordan", name: "Jordan Bryce" },
        cart: [],
        tipPercent: 0,
        status: "awaiting",
        paid: false,
      },
      {
        id: "pos-ticket-484",
        number: 484,
        client: null,
        cart: cloneCart(WALK_IN_CART),
        tipPercent: 25,
        status: "walk-in",
        paid: false,
      },
    ],
    activeTicketId: "pos-ticket-482",
    nextNumber: 485,
  };
}

function mergeCartItem(cart: CartItem[], item: CatalogItem): CartItem[] {
  const existing = cart.find((line) => line.id === item.id);
  if (existing) {
    return cart.map((line) => (line.id === item.id ? { ...line, qty: line.qty + 1 } : line));
  }
  return [...cart, { ...item, qty: 1 }];
}

function updateActiveTicket(
  tickets: PosTicket[],
  activeTicketId: string,
  updater: (ticket: PosTicket) => PosTicket,
): PosTicket[] {
  return tickets.map((ticket) => (ticket.id === activeTicketId ? updater(ticket) : ticket));
}

export function selectActiveTicket(
  state: Pick<PosStoreState, "tickets" | "activeTicketId">,
): PosTicket | undefined {
  return state.tickets.find((ticket) => ticket.id === state.activeTicketId);
}

export const usePosStore = create<PosStoreState>((set, get) => ({
  ...createInitialPosState(),

  setActiveTicket: (id) => {
    if (!get().tickets.some((ticket) => ticket.id === id)) {
      return;
    }
    set({ activeTicketId: id });
  },

  addTicket: () => {
    const { nextNumber } = get();
    const ticket = createWalkInTicket(nextNumber);
    set((state) => ({
      tickets: [...state.tickets, ticket],
      activeTicketId: ticket.id,
      nextNumber: nextNumber + 1,
    }));
  },

  createTicketFromAppointment: (input) => {
    const { nextNumber } = get();
    const client =
      input.clientId && input.clientName ? { id: input.clientId, name: input.clientName } : null;
    const ticket: PosTicket = {
      id: `pos-ticket-${nextNumber}`,
      number: nextNumber,
      client,
      cart: [buildCheckoutCartLine(input)],
      tipPercent: DEFAULT_TIP_PERCENT,
      status: client ? "active" : "walk-in",
      paid: false,
    };
    set((state) => ({
      tickets: [...state.tickets, ticket],
      activeTicketId: ticket.id,
      nextNumber: nextNumber + 1,
    }));
  },

  removeTicket: (id) => {
    const { tickets, activeTicketId } = get();
    const index = tickets.findIndex((ticket) => ticket.id === id);
    if (index === -1) {
      return;
    }

    const remaining = tickets.filter((ticket) => ticket.id !== id);
    if (remaining.length === 0) {
      const { nextNumber } = get();
      const freshWalkIn = createWalkInTicket(nextNumber);
      set({
        tickets: [freshWalkIn],
        activeTicketId: freshWalkIn.id,
        nextNumber: nextNumber + 1,
      });
      return;
    }

    let nextActiveId = activeTicketId;
    if (activeTicketId === id) {
      const nextIndex = Math.min(index, remaining.length - 1);
      nextActiveId = remaining[nextIndex]?.id ?? remaining[0]?.id ?? activeTicketId;
    }

    set({ tickets: remaining, activeTicketId: nextActiveId });
  },

  addItem: (item) => {
    const { activeTicketId, tickets } = get();
    if (!tickets.some((ticket) => ticket.id === activeTicketId)) {
      return;
    }
    set((state) => ({
      tickets: updateActiveTicket(state.tickets, activeTicketId, (ticket) => ({
        ...ticket,
        cart: mergeCartItem(ticket.cart, item),
      })),
    }));
  },

  removeItem: (itemId) => {
    const { activeTicketId, tickets } = get();
    if (!tickets.some((ticket) => ticket.id === activeTicketId)) {
      return;
    }
    set((state) => ({
      tickets: updateActiveTicket(state.tickets, activeTicketId, (ticket) => ({
        ...ticket,
        cart: ticket.cart.filter((line) => line.id !== itemId),
      })),
    }));
  },

  setTip: (percent) => {
    const { activeTicketId, tickets } = get();
    if (!tickets.some((ticket) => ticket.id === activeTicketId)) {
      return;
    }
    set((state) => ({
      tickets: updateActiveTicket(state.tickets, activeTicketId, (ticket) => ({
        ...ticket,
        tipPercent: normalizeTipPercent(percent),
      })),
    }));
  },

  attachClient: (client) => {
    const { activeTicketId } = get();
    set((state) => ({
      tickets: updateActiveTicket(state.tickets, activeTicketId, (ticket) => {
        if (ticket.status !== "walk-in" || ticket.client !== null) {
          return ticket;
        }
        return { ...ticket, client, status: "active" };
      }),
    }));
  },
}));

export function resetPosStore(): void {
  usePosStore.setState(createInitialPosState());
}
